import { Badge, Button, Flex, Switch, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { EMPTY_CELL_VALUE, EmptyCell } from '../../../../components/EmptyCell'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { useHasRole, useAppToast, usePendingRows } from '../../../../helpers'
import { IPromoBannerRaw } from '../../../../interfaces/PromoBanners/IPromoBanner'
import { PromoBannersService } from '../../../../services/PromoBanners/PromoBannersService'
import PromoBannerCoursesModal from './PromoBannerCoursesModal'
import PromoBannerModal from './PromoBannerModal'

export default function PromoBannersPage() {
  const service = new PromoBannersService()
  const toast = useAppToast()
  const [banners, setBanners] = useState<IPromoBannerRaw[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<IPromoBannerRaw | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState<IPromoBannerRaw | null>(null)
  const [coursesModalBanner, setCoursesModalBanner] = useState<IPromoBannerRaw | null>(null)
  const hasRole = useHasRole()
  const permissions = {
    view: hasRole('view_promo_banners'),
    add: hasRole('add_promo_banner'),
    edit: hasRole('edit_promo_banner'),
    delete: hasRole('delete_promo_banner'),
    toggleActive: hasRole('edit_promo_banner')
  }

  useEffect(() => {
    if (!permissions.view) return
    // A busca já chega debounced do `FilterSearch`.
    loadBanners()
  }, [search, page, pageSize, permissions.view])

  const loadBanners = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await service.list({ search, page, per_page: pageSize })
      setBanners(response.data)
      setTotalPages(response.last_page ?? 1)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os banners', error)
    } finally {
      setLoading(false)
    }
  }

  const savingRow = usePendingRows()

  const toggleActive = async (banner: IPromoBannerRaw) => {
    try {
      await service.toggleActive(banner.id)
      toast.success(banner.active ? 'Banner desativado' : 'Banner ativado')
      setBanners((prev) => prev.map((b) => (b.id === banner.id ? { ...b, active: !b.active } : b)))
    } catch {
      toast.error('Erro ao alterar status do banner')
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await service.delete(deleting.id)
      toast.success('Banner excluído', 'O banner foi excluído com sucesso.')
      setIsDeleteOpen(false)
      setDeleting(null)
      loadBanners()
    } catch {
      toast.error('Erro ao excluir banner')
    }
  }

  const timerLabel = (banner: IPromoBannerRaw) => {
    if (banner.end_date) return 'Data fixa'
    if (banner.hours_refresh) return `A cada ${banner.hours_refresh}h`
    if (banner.days_refresh) return `A cada ${banner.days_refresh}d`
    return EMPTY_CELL_VALUE
  }

  if (!permissions.view) {
    return (
      <Flex alignItems="center" justifyContent="center" h="200px">
        <Text color="gray.500">Você não tem permissão para visualizar os banners promocionais.</Text>
      </Flex>
    )
  }

  return (
    <>
      <FilterBar
        onClear={() => {
          setSearch('')
          setPage(1)
        }}
        isDirty={search !== ''}
      >
        <FilterSearch
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          placeholder="Buscar por banner"
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Texto do CTA</Th>
              <Th>Desconto</Th>
              <Th>Cronômetro</Th>
              <Th>Cursos vinculados</Th>
              <Th>Ativo</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="banners" onRetry={loadBanners} />
            ) : banners.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="banner" />
            ) : (
              banners.map((banner) => (
                <Tr key={banner.id}>
                  <Td>{banner.id}</Td>
                  <Td>{banner.cta_text}</Td>
                  <Td>
                    <EmptyCell value={banner.discount_value != null ? `${banner.discount_value}%` : null} />
                  </Td>
                  <Td>{timerLabel(banner)}</Td>
                  <Td>
                    {!banner.courses?.length ? (
                      <EmptyCell />
                    ) : (
                      <Flex gap={1} flexWrap="wrap" alignItems="center">
                        {banner.courses.slice(0, 2).map((course) => (
                          <Badge key={course.id} colorScheme="blue" variant="subtle">
                            {course.name}
                          </Badge>
                        ))}
                        {banner.courses.length > 2 && (
                          <Button
                            colorScheme="gray"
                            variant="ghost"
                            size="xs"
                            minW="auto"
                            h="auto"
                            px={2}
                            py={1}
                            fontSize="xs"
                            fontWeight="semibold"
                            onClick={() => setCoursesModalBanner(banner)}
                            _hover={{ opacity: 0.75 }}
                          >
                            +{banner.courses.length - 2} mais
                          </Button>
                        )}
                      </Flex>
                    )}
                  </Td>
                  <Td>
                    <Switch
                      isChecked={banner.active}
                      onChange={() => savingRow.run(banner.id, () => toggleActive(banner))}
                      isDisabled={!permissions.toggleActive || savingRow.isPending(banner.id)}
                    />
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      {permissions.edit && (
                        <ActionButton
                          action="edit"
                          label="Editar banner"
                          mr={0}
                          onClick={() => {
                            setEditing(banner)
                            setIsFormOpen(true)
                          }}
                        />
                      )}
                      {permissions.delete && (
                        <ActionButton
                          action="delete"
                          label="Excluir banner"
                          mr={0}
                          onClick={() => {
                            setDeleting(banner)
                            setIsDeleteOpen(true)
                          }}
                        />
                      )}
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(p) => setPage(p)}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      <PromoBannerModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        editing={editing}
        onSave={() => {
          setIsFormOpen(false)
          loadBanners()
        }}
      />

      <PromoBannerCoursesModal
        isOpen={!!coursesModalBanner}
        onClose={() => setCoursesModalBanner(null)}
        bannerText={coursesModalBanner?.cta_text}
        courses={coursesModalBanner?.courses}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false)
          setDeleting(null)
        }}
        onConfirm={handleDelete}
        action="delete"
        entity="banner"
        name={deleting?.cta_text}
        warning="Os cursos vinculados serão desvinculados automaticamente."
      />

      {permissions.add && (
        <ActionFab
          label="Novo banner promocional"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        />
      )}
    </>
  )
}
